import { formatDate, getDateInLocalTZFromDateStr, getDateString } from "../datetime/core";
import { formatScopeNotesWithExtraSections } from "./scopes";
import { WORKER_ROLE_COLORS, WORKER_ROLE_COLORS_DARK } from "../../config";
import { getUserFullName } from "./user";

/**
 * Returns worker assignments as a formatted array of worker names as strings.
 * Takes an array of scopes and returns an array that represents a week (len 7).
 * Each position in the array contains a dictionary of worker assignments for that day.
 * The dict contains a list of each worker for that day, a list of workers for each
 * job/scope combo, and a list of workers who have been assigned as off work.
 *
 * @param {Array} scopes - The array of scopes to get worker assignments from.
 * @param {Array} jobs - The array of jobs to get default details from, if not in scope.
 *
 * @returns {Array} - The array of worker assignments for the week.
 *
 */
const formatWorkerAssignments = (scopes, jobs) => {
  const week = Array(7)
    .fill(null)
    .map(() => ({
      allWorkers: [],
      workerAssignments: [],
      offWork: [],
    }));

  scopes.forEach((scope) => {
    const date = new Date(scope.date);
    const job = jobs.find((job) => job.id === scope.job.id);
    const dayOfWeek = date.getDay();
    const day = week[dayOfWeek];
    day.allWorkers.push(...scope.worker_assignment);
    day.workerAssignments.push({
      job: `${scope.job.number} - ${scope.job.description}`,
      date: scope.date,
      startTime: scope.custom_time
        ? scope.custom_time
        : job
          ? job.default_time
          : "No time set",
      address: job
        ? scope.custom_address
          ? scope.custom_address
          : job.default_address
            ? job.default_address.address
            : "No address set"
        : "No address set",
      scope: scope.scope ? scope.scope.title : "No scope set",
      allWorkers: [...scope.worker_assignment],
    });
  });
  return week;
};

/**
 * Generates SMS messages for workers based on their assignments.
 *
 * @param {Object} workers - The object containing worker assignments.
 * @param {Array} workers.allWorkers - The array of all worker names.
 * @param {Array} workers.workerAssignments - The array of worker assignments.
 *
 * @returns {Array} - The array of SMS messages for the workers.
 */
const getWorkerSMSMessages = (workers) => {
  if (!workers.allWorkers || !workers.workerAssignments) return [];
  const messages = [];
  workers.allWorkers.forEach((worker) => {
    workers.workerAssignments.forEach((scope) => {
      if (scope.allWorkers.includes(worker)) {
        const date = getDateInLocalTZFromDateStr(scope.date);
        const startTime = scope.startTime.split(':').slice(0, 2).join(':');
        const message = {
          worker: worker,
          message: `Hello ${worker.first}, you are scheduled to work ${scope.scope} on ${formatDate(date, "MM/DD/YY")}. The address for this job is ${scope.address}. Please arrive for a ${startTime} start.\n\n${scope.notes}If you have any questions, please contact your manager, this number is not monitored. Thank you!\n\n- R&S Construction Management, Inc.`,
        };
        messages.push(message);
      }
    });
  });
  return messages;
};

/**
 * Returns worker assignments as a formatted array of worker names as strings.
 * Takes an array of scopes and returns an array that represents a week (len 7).
 * Each position in the array contains a dictionary of worker assignments for that day.
 * The dict contains a list of each worker for that day, a list of workers for each
 * job/scope combo, and a list of workers who have been assigned as off work.
 */
function workerAssignmentsByDay(datesOfWeek, scopes, jobs) {
  const scopesByDate = Map.groupBy(scopes ?? [], ({ date }) => date);
  const jobsById = Map.groupBy(jobs ?? [], (job) => job.id);

  return datesOfWeek.map((date) => {
    // Can't continue if scopes and jobs are empty
    if (jobsById.size === 0) {
      return {
        allWorkers: [],
        workerAssignments: [],
        offWork: [],
      };
    }

    const scopes = (scopesByDate.get(getDateString(date)) ?? []).filter(
      (scope) => !scope.skipped
    );

    const allWorkers = scopes.flatMap(
      ({ worker_assignment }) => worker_assignment
    );

    const workerAssignments = scopes.map((scope) => {
      // Here assume there is only one job per ID

      const jobEntry = jobsById.get(scope.job.id);
      const job = jobEntry
        ? jobEntry[0]
        : {
          default_time: "No time set",
          default_address: { address: "No address set" },
        };

      const customTime = scope["custom_time"] ? scope["custom_time"] : null;
      const customAddress = scope["custom_address"]
        ? scope["custom_address"]
        : null;
      const defaultTime = job["default_time"] ? job["default_time"] : null;
      const defaultAddress = job["default_address"]
        ? job["default_address"].address
        : null;

      const combinedNotes = formatScopeNotesWithExtraSections(
        scope.notes,
        scope.extra_data_sections
      );
      return {
        job: `${scope.job.number} - ${scope.job.description}`,
        date: scope.date,
        startTime: customTime || defaultTime,
        address: customAddress || defaultAddress,
        scope: scope.scope ? scope.scope.title : "No scope set",
        notes: combinedNotes ? `Job notes: ${combinedNotes}\n\n` : "",
        allWorkers: scope["worker_assignment"],
      };
    });

    return {
      allWorkers,
      workerAssignments,
      offWork: [],
    };
  });
}

/**
 * Resolves the worker role style class using union type first, then first matching
 * dispatch worker_type, then a default class.
 *
 * @param {Object} worker
 * @param {string} defaultClass
 * @param {boolean} isDark
 * @returns {string}
 */
function getWorkerRoleColorClass(
  worker,
  defaultClass = "bg-gray-300",
  isDark = false
) {
  const colors = isDark ? WORKER_ROLE_COLORS_DARK : WORKER_ROLE_COLORS;
  if (!worker || typeof worker !== "object") {
    return defaultClass;
  }

  if (typeof worker.union_worker_type === "number") {
    return colors[worker.union_worker_type];
  }

  const unionTypeId = worker.union_worker_type?.id;
  if (unionTypeId && colors[unionTypeId]?.startsWith("bg")) {
    return colors[unionTypeId];
  }

  const workerTypes = Array.isArray(worker.worker_type) ? worker.worker_type : [];
  const fallbackType = workerTypes.find((type) => colors[type?.id]?.startsWith("bg"));
  if (fallbackType) {
    return colors[fallbackType.id];
  }

  return defaultClass;
}

const DEFAULT_WORKER_ROLE_BG_CLASS = "bg-gray-300";
const DEFAULT_WORKER_ROLE_HEX = "#d1d5db";

/**
 * @param {string|null|undefined} className Tailwind class such as `bg-[#b5c4e3]`.
 * @returns {string|null}
 */
function parseHexFromTailwindBgClass(className) {
  if (!className) {
    return null;
  }
  const match = String(className).match(/bg-\[#([0-9a-fA-F]{6})\]/i);
  return match ? `#${match[1]}` : null;
}

/**
 * @param {string} hex `#RRGGBB`
 * @param {number} factor Multiply each channel, clamped to 255 (1.15 = 15% lighter).
 * @returns {string} `rgb(r, g, b)`
 */
function lightenHexColor(hex, factor = 1.15) {
  const normalized = String(hex).replace("#", "");
  if (normalized.length !== 6) {
    return hex;
  }
  const clamp = (n) => Math.min(255, Math.round(n));
  const r = clamp(parseInt(normalized.slice(0, 2), 16) * factor);
  const g = clamp(parseInt(normalized.slice(2, 4), 16) * factor);
  const b = clamp(parseInt(normalized.slice(4, 6), 16) * factor);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * @param {string} hex `#RRGGBB`
 * @param {number} factor Multiply each channel, clamped to 0–255 (0.85 = 15% darker).
 * @returns {string} `rgb(r, g, b)`
 */
function darkenHexColor(hex, factor = 0.85) {
  const normalized = String(hex).replace("#", "");
  if (normalized.length !== 6) {
    return hex;
  }
  const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
  const r = clamp(parseInt(normalized.slice(0, 2), 16) * factor);
  const g = clamp(parseInt(normalized.slice(2, 4), 16) * factor);
  const b = clamp(parseInt(normalized.slice(4, 6), 16) * factor);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * CSS for a hatched day overlay: stripes use the worker role color (~15% lighter), gaps are transparent.
 *
 * @param {Object} worker
 * @param {string} [defaultClass] Fallback when the role has no `bg-[#…]` class (e.g. Foreman).
 * @returns {{ backgroundImage: string }}
 */
function getWorkerRoleHatchBackgroundStyle(
  worker,
  defaultClass = DEFAULT_WORKER_ROLE_BG_CLASS
) {
  const roleClass = getWorkerRoleColorClass(worker, defaultClass);
  const baseHex =
    parseHexFromTailwindBgClass(roleClass) ?? DEFAULT_WORKER_ROLE_HEX;
  const stripeColor = lightenHexColor(baseHex, 1.15);
  return {
    backgroundImage: `repeating-linear-gradient(-45deg, ${stripeColor} 0 2px, transparent 2px 4px)`,
  };
}

/**
 * First worker in `worker_assignment` that has Foreman in `worker_type`.
 * @param {Array<Object>|undefined} workerAssignment
 * @returns {Object|null}
 */
function firstForemanFromAssignment(workerAssignment) {
  if (!Array.isArray(workerAssignment)) {
    return null;
  }
  for (const w of workerAssignment) {
    const types = Array.isArray(w?.worker_type) ? w.worker_type : [];
    if (types.some((t) => t?.type === "Foreman")) {
      return w;
    }
  }
  return null;
}

/**
 * Sort comparator for worker-like objects ({ first, last }). Handles null names from the API.
 * @param {{ first?: string|null, last?: string|null }} a
 * @param {{ first?: string|null, last?: string|null }} b
 * @returns {number}
 */
function compareWorkersByName(a, b) {
  const aFirst = a?.first ?? "";
  const bFirst = b?.first ?? "";
  const byFirst = aFirst.localeCompare(bFirst);
  if (byFirst !== 0) return byFirst;
  return String(a?.last ?? "").localeCompare(String(b?.last ?? ""));
}

/** Label used in UI when a worker has no `worker_type` entries (aligned with WorkerMultiSelect). */
const WORKER_TYPE_LABEL_UNDEFINED = "Role not Defined";

/**
 * Distinct `worker_type[].type` values across workers, sorted A–Z, plus {@link WORKER_TYPE_LABEL_UNDEFINED}
 * when any worker has no typed roles.
 *
 * @param {Array<{ worker_type?: Array<{ type?: string|null }|null>|null }>|null|undefined} workers
 * @returns {string[]}
 */
function getUniqueWorkerTypeLabelsFromWorkers(workers) {
  if (!Array.isArray(workers) || workers.length === 0) {
    return [];
  }
  const labels = new Set();
  let anyWithoutTypedRole = false;
  for (const w of workers) {
    const types = Array.isArray(w?.worker_type) ? w.worker_type : [];
    const strs = types.map((t) => t?.type).filter(Boolean);
    if (strs.length === 0) {
      anyWithoutTypedRole = true;
    } else {
      strs.forEach((s) => labels.add(s));
    }
  }
  const list = [...labels].sort((a, b) => a.localeCompare(b));
  if (anyWithoutTypedRole) {
    list.push(WORKER_TYPE_LABEL_UNDEFINED);
  }
  return list;
}

/**
 * Whether `worker` belongs to the given type bucket (same rules as {@link getUniqueWorkerTypeLabelsFromWorkers}).
 *
 * @param {{ worker_type?: Array<{ type?: string|null }|null>|null }|null|undefined} worker
 * @param {string} typeLabel
 * @returns {boolean}
 */
function workerMatchesWorkerTypeLabel(worker, typeLabel) {
  if (!typeLabel) {
    return false;
  }
  const types = Array.isArray(worker?.worker_type) ? worker.worker_type : [];
  const strs = types.map((t) => t?.type).filter(Boolean);
  if (typeLabel === WORKER_TYPE_LABEL_UNDEFINED) {
    return strs.length === 0;
  }
  return strs.includes(typeLabel);
}

/**
 * Builds role label text for display, preferring union_worker_type if available.
 *
 * @param {Object} worker
 * @returns {string}
 */
function getWorkerRoleLabel(worker) {
  if (!worker || typeof worker !== "object") {
    return "";
  }

  if (worker.union_worker_type?.type) {
    return worker.union_worker_type.type;
  }

  const workerTypes = Array.isArray(worker.worker_type) ? worker.worker_type : [];
  return workerTypes.map((role) => role.type).filter(Boolean).join(", ");
}

/**
 * Short display name for a worker-like object: "First Last", or numeric id if no name.
 *
 * @param {{ id?: number|string, first?: string|null, last?: string|null }|null|undefined} w
 * @returns {string}
 */
function getWorkerDisplayLabel(w) {
  if (!w || typeof w !== "object") {
    return "Unknown";
  }
  return getUserFullName(w) || String(w.id ?? "");
}

export {
  compareWorkersByName,
  getWorkerSMSMessages,
  formatWorkerAssignments,
  workerAssignmentsByDay,
  getWorkerRoleColorClass,
  darkenHexColor,
  parseHexFromTailwindBgClass,
  getWorkerRoleHatchBackgroundStyle,
  firstForemanFromAssignment,
  getWorkerRoleLabel,
  getWorkerDisplayLabel,
  getUniqueWorkerTypeLabelsFromWorkers,
  workerMatchesWorkerTypeLabel,
  WORKER_TYPE_LABEL_UNDEFINED,
};
